import React, { useCallback } from 'react';

import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Button, Popconfirm, Space } from 'antd';

import { useDeleteWebhookConfig } from '../api';
import { IWebhookConfig } from '../types';
import { scopes } from '@/config';
import useAppState from '@/hooks/useAppState';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

interface IWebhookUrlItemActionsProps {
  config: IWebhookConfig;
  partnerId: string;
  onEdit: (config: IWebhookConfig) => void;
}

export const WebhookUrlItemActions = ({
  config,
  partnerId,
  onEdit,
}: IWebhookUrlItemActionsProps) => {
  const addToast = useToasts((state) => state.addItem);
  const userScopes = useAppState((state) => state.scopes);
  const canManage = userScopes.includes(scopes.MANAGE_WEBHOOKS);
  const { mutateAsync: deleteConfig, isPending: isDeleting } =
    useDeleteWebhookConfig({ partnerId });

  const handleDelete = useCallback(async () => {
    try {
      await deleteConfig(config.config_id);
      addToast(
        { msg: 'Webhook deleted', type: MessageType.Success },
        'root',
        {}
      );
    } catch (err) {
      addToast(
        {
          msg: err instanceof Error ? err.message : 'Delete failed',
          type: MessageType.Error,
        },
        'root',
        {}
      );
    }
  }, [deleteConfig, config.config_id, addToast]);

  if (!canManage) return null;

  return (
    <Space
      onClick={(e) => e.stopPropagation()}
      style={{ marginTop: -4, marginBottom: -4 }}
    >
      <Button
        type="text"
        icon={<EditOutlined />}
        onClick={() => onEdit(config)}
        data-testid={`webhook-edit-${config.config_id}-button`}
      />
      <Popconfirm
        title="Delete webhook URL?"
        description="This action cannot be undone."
        okText="Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
        onConfirm={handleDelete}
        disabled={isDeleting}
        icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
        rootClassName="webhook-popconfirm"
      >
        <Button
          type="text"
          icon={<DeleteOutlined />}
          danger
          loading={isDeleting}
          data-testid={`webhook-delete-${config.config_id}-button`}
        />
      </Popconfirm>
    </Space>
  );
};
